import { createContext, useContext, useEffect, useState, useCallback } from "react";

const AppContext = createContext();
const API_BASE = import.meta.env.VITE_API_URL || "";

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [fetchingUser, setFetchingUser] = useState(true);

  const [apiKeys, setApiKeys] = useState(null);
  const [models, setModels] = useState(null);
  const [globalPrompt, setGlobalPrompt] = useState("");
  const [toast, setToast] = useState(null);
  const [creditStatus, setCreditStatus] = useState(null);
  const [defaultBehavior, setDefaultBehavior] = useState("last");

  // Month selection for usage tracking
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [monthlyUsageData, setMonthlyUsageData] = useState(null);
  const [loadingMonthlyData, setLoadingMonthlyData] = useState(false);

  const [loadingKeys, setLoadingKeys] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [savingGlobalPrompt, setSavingGlobalPrompt] = useState(false);
  const [allowOthers, setAllowOthers] = useState(false);

  function ensureTimezoneSynced() {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (!tz) return;
      const key = "okeybot_tz";
      const last = localStorage.getItem(key);
      if (last === tz) return;
      fetch(`${API_BASE}/api/preferences/timezone`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ timezone: tz }),
      })
        .then((r) => {
          if (r.ok) localStorage.setItem(key, tz);
        })
        .catch(() => { });
    } catch (e) {
      if (import.meta?.env?.DEV) console.debug("TZ sync failed", e);
    }
  }

  useEffect(() => {
    let mounted = true;
    setFetchingUser(true);
    fetch(`${API_BASE}/api/user`, { credentials: "include" })
      .then(r => (r.ok ? r.json() : null))
      .then(u => {
        if (!mounted) return;
        if (u) {
          setUser(u);
          ensureTimezoneSynced();
          fetchKeys();
          fetchModels();
          fetchGlobalPrompt();
          fetchDefaultBehavior();
          fetchMonthUsage(currentMonth);
          fetchCreditStatus();
          fetchAllowOthersPreference();
        } else {
          setUser(null);
        }
      })
      .finally(() => {
        if (mounted) setFetchingUser(false);
      });
    return () => { mounted = false; };

  }, []);

  function showToast(message, type = "info") {
    setToast({ message, type });
  }

  function fetchKeys() {
    setLoadingKeys(true);
    fetch(`${API_BASE}/api/apikeys`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(keys => setApiKeys(Array.isArray(keys) ? keys : []))
      .catch(() => setApiKeys([]))
      .finally(() => setLoadingKeys(false));
  }

  function fetchModels() {
    setLoadingModels(true);
    fetch(`${API_BASE}/api/models`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(models => setModels(Array.isArray(models) ? models : []))
      .catch(() => setModels([]))
      .finally(() => setLoadingModels(false));
  }

  function fetchGlobalPrompt() {
    fetch(`${API_BASE}/api/preferences/global-prompt`, { credentials: "include" })
      .then(r => r.ok ? r.json() : { global_prompt: "" })
      .then(data => setGlobalPrompt(data.global_prompt || ""))
      .catch(() => setGlobalPrompt(""));
  }

  function fetchDefaultBehavior() {
    fetch(`${API_BASE}/api/preferences/default-behavior`, { credentials: "include" })
      .then(r => r.ok ? r.json() : { behavior: "last" })
      .then(data => setDefaultBehavior(data.behavior || "last"))
      .catch(() => setDefaultBehavior("last"));
  }

  const fetchMonthUsage = useCallback((monthValue) => {
    setLoadingMonthlyData(true);
    setMonthlyUsageData(null);
    const [year, month] = monthValue.split("-");
    const start = new Date(parseInt(year), parseInt(month) - 1, 1);
    const end = new Date(parseInt(year), parseInt(month), 0, 23, 59, 59);
    const params = new URLSearchParams({
      start: start.toISOString(),
      end: end.toISOString()
    });
    fetch(`${API_BASE}/api/analytics/overview?${params.toString()}`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setMonthlyUsageData(data); })
      .catch(() => setMonthlyUsageData(null))
      .finally(() => setLoadingMonthlyData(false));
  }, []);

  function fetchCreditStatus() {
    fetch(`${API_BASE}/api/credits`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(data => setCreditStatus(data))
      .catch(() => setCreditStatus(null));
  }

  function saveGlobalPrompt(newPrompt) {
    setSavingGlobalPrompt(true);
    trackEvent("save_global_prompt", { length: newPrompt.length });
    fetch(`${API_BASE}/api/preferences/global-prompt`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ global_prompt: newPrompt }),
    })
      .then((r) => {
        if (!r.ok) throw new Error("Failed to save");
        showToast("Global prompt saved successfully!", "success");
        setGlobalPrompt(newPrompt);
      })
      .catch(() => showToast("Failed to save global prompt", "error"))
      .finally(() => setSavingGlobalPrompt(false));
  }

  function fetchAllowOthersPreference() {
    fetch(`${API_BASE}/api/preferences/allow-others`, { credentials: "include" })
      .then(r => (r.ok ? r.json() : { allow_others: false }))
      .then(data => setAllowOthers(!!data.allow_others))
      .catch(() => setAllowOthers(false));
  }

  function saveAllowOthersPreference(val) {
    trackEvent("settings_allow_others_toggle", { enabled: val });
    fetch(`${API_BASE}/api/preferences/allow-others`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ allow_others: val }),
    })
      .then((r) => {
        if (!r.ok) throw new Error("Failed to save");
        setAllowOthers(val);
        showToast(
          val
            ? "Others can now participate in your AI chats."
            : "Only you can interact with your AI threads.",
          "success"
        );
      })
      .catch(() => showToast("Failed to update preference", "error"));
  }

  function login() {
    trackEvent("login_click", { location: "dashboard_gate" });
    const next = encodeURIComponent(window.location.pathname + window.location.search + window.location.hash);
    window.location = `${API_BASE}/auth/discord?next=${next}`;
  }

  function logout() {
    trackEvent("logout_click", {});
    fetch(`${API_BASE}/api/logout`, {
      method: "POST",
      credentials: "include",
    })
      .then(() => {
        showToast("Logged out successfully", "info");
        setTimeout(() => {
          setUser(null);
          setApiKeys(null);
          setModels([]);
          // Ensure cookie changes are reflected app-wide
          window.location.assign("/");
        }, 300);
      })
      .catch(() => showToast("Logout failed", "error"));
  }
  function trackEvent(eventName, eventData) {
    try {
      if (window.umami) {
        window.umami.track(eventName, eventData);
      }
    } catch (err) {
      // Silently fail if analytics is blocked
    }
  };

  return (
    <AppContext.Provider value={{
      user, setUser,
      fetchingUser,
      apiKeys, setApiKeys,
      models, setModels,
      creditStatus, setCreditStatus,
      globalPrompt, setGlobalPrompt,
      defaultBehavior, setDefaultBehavior,
      showToast, toast, setToast,
      loadingKeys, loadingModels, savingGlobalPrompt,
      fetchKeys, fetchModels, fetchGlobalPrompt,
      fetchMonthUsage, fetchCreditStatus,
      saveGlobalPrompt,
      allowOthers, setAllowOthers, fetchAllowOthersPreference, saveAllowOthersPreference,
      selectedMonth, setSelectedMonth,
      monthlyUsageData, setMonthlyUsageData,
      loadingMonthlyData, setLoadingMonthlyData,
      login, logout,
      trackEvent
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  return useContext(AppContext);
}
